import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Building2, Plus, Copy, Check, ShieldCheck, LogOut, KeyRound, Users, Package } from 'lucide-react';

const API = `${typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL || ''}/api`;
const TOKEN_KEY = 'superadmin_token';

// Axios instance that does NOT send auth cookies and always attaches the superadmin token
const saClient = axios.create({ withCredentials: false });

const EMPTY_FORM = {
    slug: '', name: '', vat_number: '', fiscal_code: '', address: '', city: '',
    postal_code: '', phone: '', email: '', brand_color: '#1a472a',
    admin_username: '', admin_name: '',
};

function TokenGate({ onAuthed }) {
    const [token, setToken] = useState('');
    const [checking, setChecking] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!token.trim()) return;
        setChecking(true);
        try {
            await saClient.get(`${API}/superadmin/companies`, { headers: { 'X-Superadmin-Token': token.trim() } });
            sessionStorage.setItem(TOKEN_KEY, token.trim());
            onAuthed(token.trim());
        } catch (err) {
            toast.error(err.response?.status === 401 ? 'Token non valido' : 'Onboarding non abilitato lato server');
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0F172A] p-4">
            <Card className="w-full max-w-md border-none shadow-2xl">
                <CardHeader className="text-center">
                    <div className="mx-auto w-14 h-14 rounded-2xl bg-[#1a472a] flex items-center justify-center mb-3">
                        <ShieldCheck className="h-7 w-7 text-white" />
                    </div>
                    <CardTitle className="text-2xl" style={{ fontFamily: 'Work Sans, sans-serif' }}>Console Superadmin</CardTitle>
                    <CardDescription>Inserisci il token per gestire i tenant SaaS</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="sa-token">Token Superadmin</Label>
                            <Input
                                id="sa-token"
                                type="password"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                placeholder="••••••••••••"
                                data-testid="superadmin-token-input"
                                autoFocus
                            />
                        </div>
                        <Button type="submit" className="w-full bg-[#1a472a] hover:bg-[#143a22]" disabled={checking} data-testid="superadmin-token-submit">
                            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Accedi'}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}

function CredentialsDialog({ result, onClose }) {
    const [copied, setCopied] = useState('');
    const copy = (label, value) => {
        navigator.clipboard.writeText(value);
        setCopied(label);
        setTimeout(() => setCopied(''), 1500);
    };
    if (!result) return null;
    const rows = [
        { label: 'Username admin', value: result.admin.username },
        { label: 'Password temporanea', value: result.admin.temp_password },
        { label: 'Slug tenant', value: result.company.slug },
    ];
    return (
        <Dialog open={!!result} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="superadmin-credentials-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#1a472a]" /> Tenant creato</DialogTitle>
                    <DialogDescription>
                        Copia queste credenziali <strong>adesso</strong>. La password è mostrata una sola volta. L'admin dovrà cambiarla al primo accesso.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    {rows.map((r) => (
                        <div key={r.label} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#F2F5F2] border border-[#CBD5E1]">
                            <div className="min-w-0">
                                <p className="text-xs text-[#64748B]">{r.label}</p>
                                <p className="font-mono text-sm text-[#0F172A] truncate">{r.value}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => copy(r.label, r.value)} data-testid={`copy-${r.label.replace(/\s+/g, '-').toLowerCase()}`}>
                                {copied === r.label ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                            </Button>
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <Button className="bg-[#1a472a] hover:bg-[#143a22]" onClick={onClose} data-testid="superadmin-credentials-close">Ho salvato le credenziali</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function SuperadminPage() {
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [created, setCreated] = useState(null);
    const [pendingDeactivate, setPendingDeactivate] = useState(null);

    const authHeaders = useCallback(() => ({ headers: { 'X-Superadmin-Token': token } }), [token]);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const res = await saClient.get(`${API}/superadmin/companies`, authHeaders());
            setCompanies(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                toast.error('Sessione scaduta, reinserisci il token');
                sessionStorage.removeItem(TOKEN_KEY);
                setToken('');
            } else {
                toast.error('Errore nel caricamento tenant');
            }
        } finally {
            setLoading(false);
        }
    }, [token, authHeaders]);

    useEffect(() => { load(); }, [load]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submitCreate = async (e) => {
        e.preventDefault();
        if (!form.slug.trim() || !form.name.trim()) {
            toast.error('Slug e nome azienda sono obbligatori');
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form, email: form.email || null };
            Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k]; });
            payload.slug = form.slug.trim().toLowerCase();
            payload.name = form.name.trim();
            const res = await saClient.post(`${API}/superadmin/companies`, payload, authHeaders());
            setCreated(res.data);
            setCreateOpen(false);
            setForm(EMPTY_FORM);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Errore nella creazione del tenant');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (c) => {
        try {
            await saClient.patch(`${API}/superadmin/companies/${c.id}`, { active: !c.active }, authHeaders());
            toast.success(!c.active ? 'Tenant attivato' : 'Tenant disattivato');
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Errore nel cambio stato');
        }
    };

    const onToggleRequest = (c) => {
        // Attivare è sicuro: procedi subito. Disattivare blocca il login → chiedi conferma.
        if (c.active) {
            setPendingDeactivate(c);
        } else {
            toggleActive(c);
        }
    };

    const logout = () => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken('');
        setCompanies([]);
    };

    if (!token) return <TokenGate onAuthed={setToken} />;

    return (
        <div className="min-h-screen bg-[#F2F5F2]">
            <header className="bg-[#0F172A] text-white">
                <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#1a472a] flex items-center justify-center">
                            <ShieldCheck className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="font-bold text-lg leading-tight" style={{ fontFamily: 'Work Sans, sans-serif' }}>Console Superadmin</h1>
                            <p className="text-xs text-white/60">Gestione tenant SaaS</p>
                        </div>
                    </div>
                    <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10" onClick={logout} data-testid="superadmin-logout">
                        <LogOut className="h-4 w-4 mr-2" /> Esci
                    </Button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>Tenant</h2>
                        <p className="text-sm text-[#64748B]">{companies.length} aziende registrate</p>
                    </div>
                    <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-[#1a472a] hover:bg-[#143a22]" data-testid="superadmin-new-tenant-btn">
                                <Plus className="h-4 w-4 mr-2" /> Nuovo tenant
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="superadmin-create-dialog">
                            <DialogHeader>
                                <DialogTitle>Crea nuovo tenant</DialogTitle>
                                <DialogDescription>Verrà creata l'azienda e un utente admin con password temporanea.</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={submitCreate} className="space-y-4 py-2">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <Label htmlFor="f-slug">Slug *</Label>
                                        <Input id="f-slug" value={form.slug} onChange={(e) => setField('slug', e.target.value)} placeholder="es. rossi-frutta" data-testid="tenant-slug-input" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="f-name">Nome azienda *</Label>
                                        <Input id="f-name" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Rossi Frutta Srl" data-testid="tenant-name-input" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="f-vat">P.IVA</Label>
                                        <Input id="f-vat" value={form.vat_number} onChange={(e) => setField('vat_number', e.target.value)} />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="f-fiscal">Codice fiscale</Label>
                                        <Input id="f-fiscal" value={form.fiscal_code} onChange={(e) => setField('fiscal_code', e.target.value)} />
                                    </div>
                                    <div className="space-y-1 col-span-2">
                                        <Label htmlFor="f-addr">Indirizzo</Label>
                                        <Input id="f-addr" value={form.address} onChange={(e) => setField('address', e.target.value)} />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="f-city">Città</Label>
                                        <Input id="f-city" value={form.city} onChange={(e) => setField('city', e.target.value)} />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="f-cap">CAP</Label>
                                        <Input id="f-cap" value={form.postal_code} onChange={(e) => setField('postal_code', e.target.value)} />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="f-phone">Telefono</Label>
                                        <Input id="f-phone" value={form.phone} onChange={(e) => setField('phone', e.target.value)} />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="f-email">Email</Label>
                                        <Input id="f-email" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="f-admin-user">Username admin</Label>
                                        <Input id="f-admin-user" value={form.admin_username} onChange={(e) => setField('admin_username', e.target.value)} placeholder="auto: admin-slug" data-testid="tenant-admin-username-input" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="f-admin-name">Nome admin</Label>
                                        <Input id="f-admin-name" value={form.admin_name} onChange={(e) => setField('admin_name', e.target.value)} />
                                    </div>
                                    <div className="space-y-1 col-span-2">
                                        <Label htmlFor="f-color">Colore brand</Label>
                                        <div className="flex items-center gap-3">
                                            <input id="f-color" type="color" value={form.brand_color} onChange={(e) => setField('brand_color', e.target.value)} className="h-10 w-16 rounded border border-[#CBD5E1] cursor-pointer" data-testid="tenant-color-input" />
                                            <span className="font-mono text-sm text-[#64748B]">{form.brand_color}</span>
                                        </div>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
                                    <Button type="submit" className="bg-[#1a472a] hover:bg-[#143a22]" disabled={saving} data-testid="tenant-create-submit">
                                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crea tenant'}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                <Card className="border border-[#CBD5E1] shadow-none">
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#1a472a]" /></div>
                        ) : companies.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <Building2 className="h-10 w-10 text-[#CBD5E1] mb-3" />
                                <p className="text-[#64748B]">Nessun tenant ancora. Crea il primo.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table data-testid="superadmin-companies-table">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Azienda</TableHead>
                                            <TableHead>Slug</TableHead>
                                            <TableHead>Admin</TableHead>
                                            <TableHead className="text-center">Clienti</TableHead>
                                            <TableHead className="text-center">Ordini</TableHead>
                                            <TableHead className="text-center">Stato</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {companies.map((c) => (
                                            <TableRow key={c.id} data-testid={`company-row-${c.slug}`}>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <span className="inline-block w-4 h-4 rounded" style={{ backgroundColor: c.brand_color || '#1a472a' }} />
                                                        <div>
                                                            <p className="font-medium text-[#0F172A]">{c.name}</p>
                                                            {c.subscription_status ? (
                                                                <p className="text-xs text-[#64748B]">{c.plan === 'yearly' ? 'Annuale' : c.plan === 'monthly' ? 'Mensile' : ''} · {c.subscription_status}</p>
                                                            ) : (c.vat_number ? <p className="text-xs text-[#64748B]">P.IVA {c.vat_number}</p> : null)}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell><Badge variant="secondary" className="font-mono">{c.slug}</Badge></TableCell>
                                                <TableCell className="text-sm text-[#64748B]">{c.admin_username || '—'}</TableCell>
                                                <TableCell className="text-center">
                                                    <span className="inline-flex items-center gap-1 text-sm text-[#0F172A]"><Users className="h-3.5 w-3.5 text-[#64748B]" />{c.clients_count}</span>
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    <span className="inline-flex items-center gap-1 text-sm text-[#0F172A]"><Package className="h-3.5 w-3.5 text-[#64748B]" />{c.orders_count}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center justify-center gap-2">
                                                        <Switch checked={c.active} onCheckedChange={() => onToggleRequest(c)} data-testid={`toggle-active-${c.slug}`} />
                                                        <span className={`text-xs ${c.active ? 'text-green-700' : 'text-[#B71C1C]'}`}>{c.active ? 'Attivo' : 'Sospeso'}</span>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>

            <CredentialsDialog result={created} onClose={() => setCreated(null)} />

            <AlertDialog open={!!pendingDeactivate} onOpenChange={(o) => { if (!o) setPendingDeactivate(null); }}>
                <AlertDialogContent data-testid="deactivate-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Disattivare "{pendingDeactivate?.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Gli utenti di questo tenant <strong>non potranno più accedere</strong> finché non lo riattivi.
                            {pendingDeactivate ? ` (${pendingDeactivate.clients_count} clienti, ${pendingDeactivate.orders_count} ordini)` : ''}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="deactivate-cancel">Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-[#B71C1C] hover:bg-[#8f1414]"
                            data-testid="deactivate-confirm"
                            onClick={() => { const c = pendingDeactivate; setPendingDeactivate(null); if (c) toggleActive(c); }}
                        >
                            Disattiva
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
